import React, { memo } from 'react';
import { Card } from '@nexus/lib-react/dist/core/Card/Card';
import ReturnsStatus from './ReturnsStatus';
import ReturnsStatusInfo from './ReturnsStatusInfo';
import { ClaimOverviewWithReturnsDto, ReturnItemDetails } from '../../../../../typings/returns';
import useGetReturnsStatusKeyName from '../../hooks/useGetReturnsStatusKeyName';
import makePrice from '../../utils/makePrice';

import S from './style/ReturnsAdBlock';

interface ReturnsAdBlockProps {
  returnItem: ReturnItemDetails;
  onAdPreviewClick?: (returnId: string) => void;
}

interface ClaimsAdBlockProps {
  claimItem: ClaimOverviewWithReturnsDto;
  onAdPreviewClick?: (returnId: string) => void;
}

const IMAGE_SIZE = '512';
const LOCALE_LANG = 'pl-PL';

const ReturnsAdBlockMemo: React.FC<ReturnsAdBlockProps> = ({ returnItem, onAdPreviewClick }) => {
  const { presentation, adInfo, actionDeadline, id, status } = returnItem;
  // 'status' variable is for keeping backward compatibility with prev backend responses
  const statusKey = presentation?.statusKey ?? status;
  const postingDeadlineStart = returnItem.delivery?.posting?.window?.start;
  const postingDeadlineEnd = returnItem.delivery?.posting?.window?.end;

  const statusKeyName = useGetReturnsStatusKeyName(statusKey);
  const statusInfoKeyName = useGetReturnsStatusKeyName(statusKey, true);

  const price = makePrice(LOCALE_LANG);
  const formattedPrice = price.parseAndFormat(adInfo.price.amount, adInfo.price.currency);

  const image = returnItem.adInfo?.image?.replace(/{width}|{height}/g, IMAGE_SIZE);

  const onClickHandler = () => {
    if (onAdPreviewClick) {
      return onAdPreviewClick(returnItem.id);
    }

    window.location.href = returnItem.adInfo.url;
    return null;
  };

  return (
    <>
      <S.Block onClick={onClickHandler} data-cy="returns-ad-block">
        <Card
          type="list"
          title={
            <S.Link data-cy="returns-ad-title" onClick={onClickHandler}>
              {returnItem.adInfo.title}
            </S.Link>
          }
          price={formattedPrice}
          location=""
          date=""
          isFavorite
          favoriteAction={onClickHandler}
          renderFavorite={() => onAdPreviewClick && <S.ChevronIcon />}
          renderThumbnail={props => <Card.Thumbnail type="list" thumbnail={image} {...props} />}
        />
      </S.Block>
      <ReturnsStatus textKeyName={statusKeyName} />
      <ReturnsStatusInfo
        textKeyName={statusInfoKeyName}
        actionDeadline={actionDeadline}
        postingDeadlineStart={postingDeadlineStart}
        postingDeadlineEnd={postingDeadlineEnd}
        returnId={id}
      />
    </>
  );
};

const ClaimsAdBlockMemo: React.FC<ClaimsAdBlockProps> = ({ claimItem, onAdPreviewClick }) => {
  const { adDetails, returnEntries, presentation, disputeStatus } = claimItem;
  // 'disputeStatus' variable is for keeping backward compatibility with prev backend responses
  const statusKey =
    returnEntries[0]?.presentation?.statusKey ?? presentation?.statusKey ?? disputeStatus;
  const actionDeadline = returnEntries[0]?.actionDeadline;
  const postingDeadlineStart = returnEntries[0]?.delivery?.postingWindowOverview?.start;
  const postingDeadlineEnd = returnEntries[0]?.delivery?.postingWindowOverview?.end;

  const statusKeyName = useGetReturnsStatusKeyName(statusKey);
  const statusInfoKeyName = useGetReturnsStatusKeyName(statusKey, true);

  const price = makePrice(LOCALE_LANG);
  const formattedPrice = price.parseAndFormat(adDetails.price.cents, adDetails.price.currency);

  const image = adDetails.imageUrl?.replace(/{width}|{height}/g, IMAGE_SIZE);
  const isThisReturn = !!returnEntries.length;

  const onClickHandler = () => {
    if (isThisReturn && onAdPreviewClick) {
      return onAdPreviewClick(returnEntries[0].id);
    }
    window.location.href = adDetails.url;
    return null;
  };

  return (
    <>
      <S.Block onClick={onClickHandler} data-cy="returns-ad-block">
        <Card
          type="list"
          title={
            <S.Link data-cy="returns-ad-title" onClick={onClickHandler}>
              {adDetails.title}
            </S.Link>
          }
          price={formattedPrice}
          location=""
          date=""
          isFavorite
          favoriteAction={onClickHandler}
          renderFavorite={() => onAdPreviewClick && <S.ChevronIcon />}
          renderThumbnail={props => <Card.Thumbnail type="list" thumbnail={image} {...props} />}
        />
      </S.Block>
      <ReturnsStatus textKeyName={statusKeyName} />
      <ReturnsStatusInfo
        textKeyName={statusInfoKeyName}
        actionDeadline={actionDeadline}
        postingDeadlineStart={postingDeadlineStart}
        postingDeadlineEnd={postingDeadlineEnd}
        returnId={isThisReturn ? returnEntries[0].id : null}
      />
    </>
  );
};

const ReturnsAdBlock = memo(ReturnsAdBlockMemo);
const ClaimsAdBlock = memo(ClaimsAdBlockMemo);

export { ReturnsAdBlock, ClaimsAdBlock };
